import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Card,
  useTheme,
  alpha,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Avatar,
  Fade
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  Business,
  Domain,
  Group,
  LocationOn,
  TrendingUp,
  AutoAwesome,
  Speed,
  CheckCircle
} from '@mui/icons-material';
import { useTranslate } from '@refinedev/core';
import { useAuth } from '../../../contexts/auth-context';
import axiosInstance from '../../../api/axios-config';

interface CompanySetupWalaxyProps {
  onComplete: (data: any) => void;
}

export const CompanySetupWalaxy: React.FC<CompanySetupWalaxyProps> = ({ onComplete }) => {
  const theme = useTheme();
  const translate = useTranslate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  // Pre-fill data in dev mode
  const isDev = process.env.NODE_ENV === 'development';

  const [formData, setFormData] = useState({
    companyName: isDev ? 'Safee Test Company' : '',
    industry: isDev ? 'securite' : '',
    companySize: isDev ? '51-200' : '',
    address: isDev ? '123 Avenue des Champs-Élysées, 75008 Paris' : ''
  });
  const [completed, setCompleted] = useState({
    name: false,
    industry: false,
    size: false
  });

  const industries = [
    { value: 'construction', label: 'Construction et BTP', icon: '🏗️' },
    { value: 'transport', label: 'Transport et Logistique', icon: '🚚' },
    { value: 'sante', label: 'Santé et Services sociaux', icon: '🏥' },
    { value: 'securite', label: 'Sécurité privée', icon: '🛡️' },
    { value: 'industrie', label: 'Industrie et Manufacturing', icon: '🏭' },
    { value: 'services', label: 'Services aux entreprises', icon: '💼' },
    { value: 'commerce', label: 'Commerce et Distribution', icon: '🛍️' },
    { value: 'autre', label: 'Autre', icon: '🔧' }
  ];

  const companySizes = [
    { value: '1-10', label: '1-10', emoji: '👥', description: 'Petite équipe' },
    { value: '11-50', label: '11-50', emoji: '👫', description: 'PME' },
    { value: '51-200', label: '51-200', emoji: '🏢', description: 'ETI' },
    { value: '200+', label: '200+', emoji: '🌐', description: 'Grande entreprise' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axiosInstance.post('/company/create-and-assign', {
        nom: formData.companyName,
        industry: formData.industry,
        size: formData.companySize,
        address: formData.address,
        limitUser: formData.companySize === '200+' ? 1000 : parseInt(formData.companySize.split('-')[1])
      });

      console.log('Company created response:', response.data);

      // Ensure we're passing the company object with its ID
      const companyData = response.data.company || response.data;
      onComplete(companyData);
    } catch (err: any) {
      console.error('Error creating company:', err);
      setLoading(false);
    }
  };

  const updateField = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (value) {
      setCompleted({ ...completed, [field.replace('company', '').toLowerCase()]: true });
    }
  };

  const completionRate = Object.values(completed).filter(Boolean).length / 3 * 100;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>
            🏢
          </Typography>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
            Commençons par votre entreprise
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Quelques informations pour personnaliser votre expérience
          </Typography>
        </Box>
      </motion.div>

      {/* Progress Indicator */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="caption" fontWeight={600}>
            Progression
          </Typography>
          <Typography variant="caption" color="primary" fontWeight={600}>
            {Math.round(completionRate)}%
          </Typography>
        </Box>
        <Box sx={{ height: 8, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 4, overflow: 'hidden' }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${completionRate}%` }}
            transition={{ duration: 0.5 }}
            style={{
              height: '100%',
              background: `linear-gradient(90deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
              borderRadius: 4
            }}
          />
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Company Name */}
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card
                sx={{
                  p: 3,
                  border: `2px solid ${completed.name ? theme.palette.success.main : 'transparent'}`,
                  transition: 'all 0.3s ease',
                  background: completed.name ? alpha(theme.palette.success.main, 0.05) : 'transparent'
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                    <Business />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      Nom de votre entreprise
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Comment s'appelle votre société ?
                    </Typography>
                  </Box>
                  {completed.name && (
                    <Fade in>
                      <CheckCircle color="success" />
                    </Fade>
                  )}
                </Stack>
                <TextField
                  fullWidth
                  value={formData.companyName}
                  onChange={(e) => updateField('companyName', e.target.value)}
                  placeholder="Ex: ACME Corp"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Domain color="action" />
                      </InputAdornment>
                    ),
                    sx: {
                      fontSize: '1.1rem',
                      fontWeight: 500
                    }
                  }}
                />
              </Card>
            </motion.div>
          </Grid>

          {/* Industry */}
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card
                sx={{
                  p: 3,
                  border: `2px solid ${completed.industry ? theme.palette.success.main : 'transparent'}`,
                  transition: 'all 0.3s ease',
                  background: completed.industry ? alpha(theme.palette.success.main, 0.05) : 'transparent'
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                    <TrendingUp />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      Votre secteur d'activité
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Dans quel domaine évoluez-vous ?
                    </Typography>
                  </Box>
                  {completed.industry && (
                    <Fade in>
                      <CheckCircle color="success" />
                    </Fade>
                  )}
                </Stack>
                <Grid container spacing={2}>
                  {industries.map((industry) => (
                    <Grid item xs={12} sm={6} md={3} key={industry.value}>
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Card
                          onClick={() => updateField('industry', industry.value)}
                          sx={{
                            p: 2,
                            cursor: 'pointer',
                            textAlign: 'center',
                            border: `2px solid ${formData.industry === industry.value ? theme.palette.primary.main : 'transparent'}`,
                            bgcolor: formData.industry === industry.value ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                            transition: 'all 0.2s ease',
                            height: 160,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            '&:hover': {
                              borderColor: theme.palette.primary.light,
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          <Typography variant="h2" sx={{ mb: 1 }}>
                            {industry.icon}
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {industry.label}
                          </Typography>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </motion.div>
          </Grid>

          {/* Company Size */}
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card
                sx={{
                  p: 3,
                  border: `2px solid ${completed.size ? theme.palette.success.main : 'transparent'}`,
                  transition: 'all 0.3s ease',
                  background: completed.size ? alpha(theme.palette.success.main, 0.05) : 'transparent'
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.info.main }}>
                    <Group />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      Taille de votre équipe
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Combien êtes-vous ?
                    </Typography>
                  </Box>
                  {completed.size && (
                    <Fade in>
                      <CheckCircle color="success" />
                    </Fade>
                  )}
                </Stack>
                <Grid container spacing={2}>
                  {companySizes.map((size) => (
                    <Grid item xs={12} sm={6} md={3} key={size.value}>
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Card
                          onClick={() => updateField('companySize', size.value)}
                          sx={{
                            p: 3,
                            cursor: 'pointer',
                            textAlign: 'center',
                            border: `2px solid ${formData.companySize === size.value ? theme.palette.primary.main : 'transparent'}`,
                            bgcolor: formData.companySize === size.value ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: theme.palette.primary.light,
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          <Typography variant="h2" sx={{ mb: 1 }}>
                            {size.emoji}
                          </Typography>
                          <Typography variant="h6" fontWeight={700}>
                            {size.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {size.description}
                          </Typography>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </motion.div>
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={!formData.companyName || !formData.industry || !formData.companySize || loading}
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 50,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`
                    },
                    '&:disabled': {
                      background: theme.palette.action.disabledBackground
                    }
                  }}
                >
                  {loading ? 'Création en cours...' : 'Créer mon espace 🚀'}
                </Button>

                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
                  <Chip icon={<Speed />} label="Setup rapide" size="small" />
                  <Chip icon={<AutoAwesome />} label="100% personnalisé" size="small" />
                </Stack>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
